import type { CartBundleItem, CartItem } from '@/ui/store/cart/types';
import { CartItems } from '@/ui/store/cart/types/CartItems';
import { getPriceOrInitialPeriodPrice } from '@/ui/utils/currency-helpers';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { resolveDataLayerEventName } from '@/utils/data-layer-event-names';
import { getExperimentTrackingProps } from '@/utils/experiment-helper';

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  discount?: number;
  coupon?: string;
  quantity: number;
  index?: number;
  nuki_id?: string;
}

interface EcommerceEventData {
  currency?: string;
  value?: number;
  items: GA4Item[];
  transaction_id?: string;
  tax?: number;
  shipping?: number;
  coupon?: string;
  shipping_tier?: string;
  payment_type?: string;
  remote_access_nuki_ids?: string[];
}

export type WithdrawalStep = 1 | 2 | 3;

export type WithdrawalLoginState = 'logged_out' | 'public' | 'myaccount';

export type ExchangeIdValidationStatus = 'valid' | 'invalid' | 'not_eligible' | 'exceeded';

const WITHDRAWAL_STEP_EVENTS: Record<WithdrawalStep, string> = {
  1: 'withdrawal_start',
  2: 'withdrawal_request',
  3: 'withdrawal_submit',
};

export class DataLayerHelper {
  private static initDataLayer(): void {
    globalThis.dataLayer = globalThis.dataLayer || [];
  }

  private static clearEcommerce(): void {
    globalThis.dataLayer.push({ ecommerce: null });
  }

  // Merged in the two push primitives rather than at each call site, so EVERY GTM event — purchase
  // included — carries the experiment the visitor is in. Mapping these fields on to Bloomreach is
  // GTM-side configuration owned by marketing; the dataLayer field is the channel.
  private static pushEvent(eventName: string, data?: Record<string, unknown>): void {
    DataLayerHelper.initDataLayer();
    const payload = { event: eventName, ...data, ...getExperimentTrackingProps() };
    globalThis.dataLayer.push(payload);
  }

  private static pushEcommerceEvent(eventName: string, ecommerceData: EcommerceEventData): void {
    DataLayerHelper.initDataLayer();
    DataLayerHelper.clearEcommerce();

    // Remove undefined/null values from ecommerce data
    const cleanedData: Record<string, unknown> = {};
    Object.entries(ecommerceData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleanedData[key] = value;
      }
    });

    // Experiment fields sit next to `ecommerce`, not inside it: GTM reads them as event-level custom
    // dimensions and the `ecommerce` object must keep its GA4 schema.
    const payload = { event: eventName, ecommerce: cleanedData, ...getExperimentTrackingProps() };
    globalThis.dataLayer.push(payload);
  }

  // --- Item Mappers ---

  static mapCartItemToGA4Item(item: CartItem, index: number): GA4Item {
    const ga4Item: GA4Item = {
      item_id: item.sku,
      item_name: item.name,
      item_brand: 'Nuki',
      price: item.priceVariant?.price,
      quantity: item.quantity,
      index,
    };

    if (item.discount) {
      ga4Item.discount = item.discount;
    }

    return ga4Item;
  }

  static mapCartToGA4Items(cartItems: CartItems): GA4Item[] {
    return [
      ...cartItems.products.map((p, i) => DataLayerHelper.mapCartItemToGA4Item(p, i)),
      ...cartItems.bundles.map((b, bIndex) =>
        DataLayerHelper.mapCartBundleToGA4Item(b, cartItems.products.length + bIndex),
      ),
    ];
  }

  static mapCartBundleToGA4Item(bundle: CartBundleItem, index: number): GA4Item {
    const basePrice = bundle.productVariants.reduce(
      (sum, v) =>
        sum +
        (getPriceOrInitialPeriodPrice(v.priceVariant, v.subscription?.period) || 0) * v.quantity,
      0,
    );
    const discount = bundle.productVariants.reduce(
      (sum, v) => sum + (v.discount || 0) * v.quantity,
      0,
    );

    return {
      item_id: `bundle-${bundle.documentExtRef}`,
      item_name: bundle.name,
      item_brand: 'Nuki',
      price: basePrice - discount,
      quantity: bundle.quantity,
      index,
    };
  }

  // --- Ecommerce Funnel Events ---

  static pushViewItem(items: GA4Item[], currency?: string, value?: number): void {
    DataLayerHelper.pushEcommerceEvent('view_item', {
      currency,
      value,
      items,
    });
  }

  static pushAddToCart(items: GA4Item[], currency?: string, value?: number): void {
    DataLayerHelper.pushEcommerceEvent('add_to_cart', {
      currency,
      value,
      items,
    });
  }

  static pushRemoveFromCart(items: GA4Item[], currency?: string, value?: number): void {
    DataLayerHelper.pushEcommerceEvent('remove_from_cart', {
      currency,
      value,
      items,
    });
  }

  static pushViewCart(items: GA4Item[], currency?: string, value?: number): void {
    DataLayerHelper.pushEcommerceEvent('view_cart', {
      currency,
      value,
      items,
    });
  }

  static pushBeginCheckout(
    items: GA4Item[],
    currency?: string,
    value?: number,
    coupon?: string,
  ): void {
    DataLayerHelper.pushEcommerceEvent('begin_checkout', {
      currency,
      value,
      coupon,
      items,
    });
  }

  static pushAddShippingInfo(
    items: GA4Item[],
    currency?: string,
    value?: number,
    shippingTier?: string,
  ): void {
    DataLayerHelper.pushEcommerceEvent('add_shipping_info', {
      currency,
      value,
      shipping_tier: shippingTier,
      items,
    });
  }

  static pushAddPaymentInfo(
    items: GA4Item[],
    currency?: string,
    value?: number,
    paymentType?: string,
  ): void {
    DataLayerHelper.pushEcommerceEvent('add_payment_info', {
      currency,
      value,
      payment_type: paymentType,
      items,
    });
  }

  static pushPurchase(params: {
    transactionId: string;
    value?: number;
    tax?: number;
    shipping?: number;
    currency?: string;
    coupon?: string;
    items: GA4Item[];
    remoteAccessNukiIds?: string[];
  }): void {
    DataLayerHelper.pushEcommerceEvent('purchase', {
      transaction_id: params.transactionId,
      value: params.value,
      tax: params.tax,
      shipping: params.shipping,
      currency: params.currency,
      coupon: params.coupon,
      items: params.items,
      remote_access_nuki_ids: params.remoteAccessNukiIds,
    });
  }

  // --- User Events ---

  static pushLogin(method: string, userId?: string): void {
    const payload = {
      event: 'login',
      method,
      ...(userId && { user_id: userId }),
    };
    DataLayerHelper.initDataLayer();
    globalThis.dataLayer.push(payload);
  }

  static pushSignUp(method: string): void {
    DataLayerHelper.pushEvent('sign_up', { method });
  }

  static pushUserId(userId: string): void {
    DataLayerHelper.initDataLayer();
    globalThis.dataLayer.push({ user_id: userId });
  }

  // --- Custom Events ---

  /**
   * Push an arbitrary conversion event (e.g. SERVICE_ACTIVATED, ACTIVATION_CODE_REQUESTED).
   * The internal conversion identifier is mapped to its standardized snake_case GTM event name
   * via `resolveDataLayerEventName` (see `data-layer-event-names.ts`). Conversions without a
   * mapping fall back to their raw identifier, preserving previous behaviour.
   */
  static pushConversionEvent(
    conversion: TrackingConversionType | string,
    payload: Record<string, unknown> = {},
  ): void {
    DataLayerHelper.pushEvent(resolveDataLayerEventName(conversion), payload);
  }

  // --- Standardized Funnel Conversion Events ---

  static pushQualifiedUser(): void {
    DataLayerHelper.pushConversionEvent(TrackingConversionType.QUALIFIED_USER);
  }

  static pushFormSubmit({ formId }: { formId?: string } = {}): void {
    DataLayerHelper.pushConversionEvent(
      TrackingConversionType.FORM_SUBMIT,
      formId ? { form_id: formId } : {},
    );
  }

  static pushInitiatePayment({
    cartValue,
    currency,
    paymentProvider,
  }: {
    cartValue?: number;
    currency?: string;
    paymentProvider?: string;
  } = {}): void {
    const payload: Record<string, unknown> = {};

    if (cartValue !== undefined) {
      payload.cart_value = cartValue;
    }

    if (currency !== undefined) {
      payload.currency = currency;
    }

    if (paymentProvider !== undefined) {
      payload.payment_provider = paymentProvider;
    }

    DataLayerHelper.pushConversionEvent(TrackingConversionType.INITIATE_PAYMENT, payload);
  }

  static pushApplyCoupon(coupon: string, success: boolean): void {
    DataLayerHelper.pushEvent('apply_coupon', { coupon, success });
  }

  // Plain GTM events only; routing these through trackConversion() would emit a second event.
  static pushWithdrawalStep(step: WithdrawalStep, loginState: WithdrawalLoginState): void {
    DataLayerHelper.pushEvent(WITHDRAWAL_STEP_EVENTS[step], {
      withdrawal_step: step,
      login_state: loginState,
    });
  }

  // The device ID itself stays out of the dataLayer; GTM only reads validation_status
  static pushExchangeIdValidation(
    validationStatus: ExchangeIdValidationStatus,
    exchangeProgram: string,
  ): void {
    DataLayerHelper.pushEvent('exchange_id_validation', {
      validation_status: validationStatus,
      exchange_program: exchangeProgram,
    });
  }

  static pushSelectClub(): void {
    DataLayerHelper.pushEvent('select_club');
  }

  static pushReferAFriendButtonClick(buttonName: 'copy' | 'share'): void {
    DataLayerHelper.pushEvent('button_click_raf', {
      button_name: buttonName,
      campaign_name: 'refer_a_friend',
      feature_area: 'myaccount',
    });
  }

  static pushApplyVat(success: boolean): void {
    DataLayerHelper.pushEvent('apply_vat', { success });
  }

  static pushClickCheckoutLogin(): void {
    DataLayerHelper.pushEvent('click_checkout_login');
  }

  static pushPurchaseFailed(params: {
    transactionId: string;
    value?: number;
    currency?: string;
    paymentType?: string;
    items: GA4Item[];
  }): void {
    DataLayerHelper.pushEcommerceEvent('purchase_failed', {
      transaction_id: params.transactionId,
      value: params.value,
      currency: params.currency,
      payment_type: params.paymentType,
      items: params.items,
    });
  }

  // --- Cart Item Tracking (used by CartService) ---

  static pushProductCartUpdate(
    product: CartItem,
    action: 'add' | 'remove',
    currency?: string,
  ): void {
    const item = DataLayerHelper.mapCartItemToGA4Item(product, 0);
    item.quantity = 1;
    const value = (product.priceVariant?.price || 0) - (product.discount || 0);

    if (action === 'add') {
      DataLayerHelper.pushAddToCart([item], currency, value);
    } else {
      DataLayerHelper.pushRemoveFromCart([item], currency, value);
    }
  }

  static pushBundleCartUpdate(
    bundle: CartBundleItem,
    action: 'add' | 'remove',
    currency?: string,
  ): void {
    const item = DataLayerHelper.mapCartBundleToGA4Item(bundle, 0);
    item.quantity = 1;
    const value = item.price || 0;

    if (action === 'add') {
      DataLayerHelper.pushAddToCart([item], currency, value);
    } else {
      DataLayerHelper.pushRemoveFromCart([item], currency, value);
    }
  }
}
